import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Select, Spinner, Badge, Textarea } from '../ui.jsx';

// Global contact quick-edit drawer. Any screen can open it by dispatching:
//   window.dispatchEvent(new CustomEvent('tradeops:edit-contact', { detail: { id, name } }))
// Or use the <ContactLink> component below as a drop-in replacement for any contact-name
// string. Saves on Save click; closes on outside-click / Escape.

const STAGES = ['New Lead', 'Contacted', 'Qualified', 'Booked', 'Estimate Sent', 'Invoiced', 'Paid', 'Lost'];

export function ContactQuickEditProvider() {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const handler = (e) => setOpenId(e.detail?.id || null);
    const close = (e) => { if (e.key === 'Escape') setOpenId(null); };
    window.addEventListener('tradeops:edit-contact', handler);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('tradeops:edit-contact', handler);
      window.removeEventListener('keydown', close);
    };
  }, []);

  if (!openId) return null;
  return <Drawer contactId={openId} onClose={() => setOpenId(null)}/>;
}

function Drawer({ contactId, onClose }) {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('details');
  // Touched flips true the moment the user edits ANY field. Way more reliable
  // than a deep-equality dirty check, which silently false-negatived on
  // nested-object reference equality (and made Save look broken).
  const [touched, setTouched] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    setTouched(false); setSavedAt(0);
    api(`/api/contacts/${contactId}`).then(d => { setC(d); setForm(d); }).catch(e => alert(e.message));
  }, [contactId]);

  if (!c) return null;

  const save = async () => {
    setBusy(true);
    try {
      const updated = await api(`/api/contacts/${contactId}`, { method: 'PATCH', body: form });
      setC(updated); setForm(updated); setTouched(false); setSavedAt(Date.now());
      window.dispatchEvent(new CustomEvent('tradeops:contact-updated', { detail: { id: contactId } }));
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally { setBusy(false); }
  };

  const set = (k, v) => { setForm({ ...form, [k]: v }); setTouched(true); };

  return <>
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:5000 }}/>
    <div onClick={e=>e.stopPropagation()} style={{
      position:'fixed', top:0, right:0, height:'100vh', width:480, maxWidth:'92vw',
      background:G.card, borderLeft:`1px solid ${G.border}`, zIndex:5001,
      display:'flex', flexDirection:'column', boxShadow:'-8px 0 32px rgba(0,0,0,.4)',
    }}>
      <div style={{ padding:'18px 22px', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:18, fontWeight:800 }}>{c.firstName} {c.lastName}</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
            <Badge c={G.purple} bg={G.purpleDim}>{c.stage || 'no stage'}</Badge>
            {c.value > 0 && <span style={{ marginLeft:8 }}>·  ${Number(c.value).toLocaleString()}</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          {/* Quick "Add to Pipeline" — appears when contact is currently outside the active pipeline */}
          {(!c.stage || ['Lost','Completed'].includes(c.stage) || !STAGES.includes(c.stage)) && c.stage !== 'New Lead' && <Btn sz="sm" v="primary" onClick={async () => {
            try {
              const updated = await api(`/api/contacts/${contactId}`, { method:'PATCH', body:{ stage: 'New Lead' }});
              setC(updated); setForm(updated);
              window.dispatchEvent(new CustomEvent('tradeops:contact-updated', { detail: { id: contactId } }));
            } catch (e) { alert(e.message); }
          }}>➕ Add to Pipeline</Btn>}
          <Btn sz="sm" v="ghost" onClick={onClose}>✕</Btn>
        </div>
      </div>

      <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${G.border}`, padding:'0 22px' }}>
        {[['details','Details'],['activity','Activity']].map(([k,l]) => (
          <button key={k} onClick={()=>setTab(k)} style={{
            background:'none', border:'none', padding:'12px 14px', cursor:'pointer',
            fontSize:13, fontWeight:600,
            color: tab===k ? G.purple : G.textMuted,
            borderBottom: tab===k ? `2px solid ${G.purple}` : '2px solid transparent',
          }}>{l}</button>
        ))}
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'18px 22px' }}>
        {tab === 'details' && <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <Field label="First name"><Input value={form.firstName||''} onChange={e=>set('firstName', e.target.value)}/></Field>
            <Field label="Last name"><Input value={form.lastName||''} onChange={e=>set('lastName', e.target.value)}/></Field>
          </div>
          <Field label="Phone"><Input value={form.phone||''} onChange={e=>set('phone', e.target.value)} placeholder="(813) 555-0100"/></Field>
          <Field label="Email"><Input type="email" value={form.email||''} onChange={e=>set('email', e.target.value)}/></Field>
          <Field label="Address"><Input value={form.address||''} onChange={e=>set('address', e.target.value)}/></Field>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:10 }}>
            <Field label="City"><Input value={form.city||''} onChange={e=>set('city', e.target.value)}/></Field>
            <Field label="State"><Input value={form.state||''} onChange={e=>set('state', e.target.value)} maxLength={2}/></Field>
            <Field label="ZIP"><Input value={form.zip||''} onChange={e=>set('zip', e.target.value)} maxLength={10}/></Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <Field label="Stage">
              <Select value={form.stage||'New Lead'} onChange={e=>set('stage', e.target.value)}>
                {STAGES.map(s => <option key={s}>{s}</option>)}
              </Select>
            </Field>
            <Field label="Source"><Input value={form.source||''} onChange={e=>set('source', e.target.value)}/></Field>
          </div>
          <Field label="Service"><Input value={form.service||''} onChange={e=>set('service', e.target.value)} placeholder="AC Repair, Plumbing, etc."/></Field>
          <Field label="Value (estimated $)"><Input type="number" value={form.value||0} onChange={e=>set('value', Number(e.target.value)||0)}/></Field>
          <Field label="Notes (internal)"><Textarea value={form.notes||''} onChange={e=>set('notes', e.target.value)} style={{ minHeight:80 }}/></Field>
        </>}

        {tab === 'activity' && <ActivityFeed contactId={contactId}/>}
      </div>

      <div style={{ padding:'12px 22px', borderTop:`1px solid ${G.border}`, background:G.input }}>
        <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Quick actions</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
          <Btn sz="sm" v="ghost" onClick={async () => {
            const est = await api('/api/estimates', { method: 'POST', body: { contactId, contact: `${c.firstName} ${c.lastName||''}`.trim(), title: '', taxRate: 7, lines: [{ name:'', qty:1, price:0, unit:'each' }], validUntil: new Date(Date.now()+30*864e5).toISOString().slice(0,10) }});
            window.dispatchEvent(new CustomEvent('tradeops:nav', { detail:{ tab:'estimates', editId: est.id } }));
            onClose();
          }}>+ Estimate</Btn>
          <Btn sz="sm" v="ghost" onClick={async () => {
            const inv = await api('/api/invoices', { method: 'POST', body: { contactId, contact: `${c.firstName} ${c.lastName||''}`.trim(), service: '', taxRate: 7, lines: [], status: 'draft' }});
            window.dispatchEvent(new CustomEvent('tradeops:nav', { detail:{ tab:'invoices', editId: inv.id } }));
            onClose();
          }}>+ Invoice</Btn>
          <Btn sz="sm" v="ghost" onClick={async () => {
            const job = await api('/api/jobs', { method:'POST', body: { name:'Service Visit', contactId, contact: `${c.firstName} ${c.lastName||''}`.trim(), address: c.address || '', scheduledDate: new Date().toISOString().slice(0,10), startTime:'09:00', durationMinutes:120, status:'Booked' }});
            window.dispatchEvent(new CustomEvent('tradeops:nav', { detail:{ tab:'jobs' } }));
            onClose();
          }}>+ Job</Btn>
          <Btn sz="sm" v="ghost" onClick={async () => {
            if (!c.phone) { alert('No phone on file'); return; }
            const msg = prompt(`Send SMS to ${c.firstName} ${c.lastName||''} (${c.phone}):`);
            if (msg) { await api('/api/messaging/send', { method:'POST', body:{ phone: c.phone, body: msg, contactId } }); alert('Sent'); }
          }}>💬 SMS</Btn>
          <Btn sz="sm" v="ghost" onClick={async () => {
            if (!c.phone) { alert('No phone on file'); return; }
            if (!confirm(`Have the AI agent call ${c.firstName} now?`)) return;
            try {
              await api('/api/agents/outbound-call', { method:'POST', body:{ contactId: c.id, scriptKey:'new_lead' }});
              alert('AI is calling — transcript will land in the timeline');
            } catch (e) { alert(e.message); }
          }}>🤖 Call AI</Btn>
          <Btn sz="sm" v="ghost" onClick={() => window.location.href = `tel:${c.phone}`}>📞 Call</Btn>
        </div>
      </div>

      <div style={{ padding:'14px 22px', borderTop:`1px solid ${G.border}`, display:'flex', justifyContent:'space-between', gap:10 }}>
        <Btn v="ghost" onClick={onClose}>Close</Btn>
        <div style={{ display:'flex', gap:8 }}>
          <Btn v="primary" onClick={save} disabled={busy}>
            {busy ? <Spinner sz={14}/>
              : touched ? 'Save changes'
              : savedAt && (Date.now() - savedAt) < 3000 ? '✓ Saved'
              : 'Save'}
          </Btn>
        </div>
      </div>
    </div>
  </>;
}

function ActivityFeed({ contactId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api(`/api/contact-detail/${contactId}/communications`).then(setData).catch(()=>setData([]));
  }, [contactId]);
  if (!data) return <Spinner sz={20}/>;
  if (data.length === 0) return <div style={{ color:G.textMuted, fontSize:13 }}>No activity yet.</div>;
  return <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
    {data.slice(0, 30).map(e => <div key={e.id} style={{ ...styles.card, padding:10, fontSize:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
        <Badge c={G.cyan} bg={G.cyan+'22'}>{e.channel} · {e.direction}</Badge>
        <span style={{ color:G.textMuted }}>{new Date(e.created_at).toLocaleString()}</span>
      </div>
      <div style={{ color:G.text }}>{(e.body||'').substring(0, 200)}{(e.body||'').length>200?'…':''}</div>
    </div>)}
  </div>;
}

// Drop-in replacement for any contact name. Clicking opens the quick-edit drawer.
export function ContactLink({ id, children, style, ...rest }) {
  if (!id) return <span style={style}>{children}</span>;
  return <span
    onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('tradeops:edit-contact', { detail: { id } })); }}
    style={{ cursor: 'pointer', color: 'inherit', borderBottom: `1px dotted ${G.textMuted}66`, ...style }}
    {...rest}
  >{children}</span>;
}
